import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, Clock, Tag } from "lucide-react";
import { SiteFooter } from "@/components/SiteFooter";
import { EN_INSIGHT_CATEGORY_LABEL } from "@/content/en-insights/types";
import type { EnInsightMeta } from "@/content/en-insights/meta.gen";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema } from "@/lib/schema";
import { EnHeader, EnCtaStrip, EnOfficesStrip } from "../services/index";

const TITLE = "Insights & Guides for Expats in Thailand | Thai Notary Law (Phuket)";
const DESC =
  "English-language guides on Notary Public, Apostille, marriage registration, DTV digital nomad visa, and certified translation in Thailand — written by licensed lawyers in Phuket.";

export const Route = createFileRoute("/en/insights/")({
  component: EnInsightsHub,
  loader: async (): Promise<{ posts: EnInsightMeta[] }> => {
    const { EN_INSIGHTS_META } = await import("@/content/en-insights/meta");
    return { posts: EN_INSIGHTS_META };
  },
  head: ({ loaderData }) => {
    const posts: EnInsightMeta[] = loaderData?.posts ?? [];
    const site = getActiveSiteUrl();
    const base = pageHeadForProvince({
      title: TITLE,
      description: DESC,
      path: "/en/insights",
      locale: "en_US",
      alternates: [{ lang: "th", path: "/insights" }],
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Insights", url: `${site}/en/insights` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "@id": `${site}/en/insights#collection`,
            name: "Thai Notary Insights — Phuket (English)",
            url: `${site}/en/insights`,
            inLanguage: "en-US",
            isPartOf: { "@id": `${site}/#website` },
            mainEntity: {
              "@type": "ItemList",
              numberOfItems: posts.length,
              itemListElement: posts.map((p, idx) => ({
                "@type": "ListItem",
                position: idx + 1,
                url: `${site}/en/insights/${p.slug}`,
                name: p.title,
              })),
            },
          }),
        },
      ],
    };
  },
});

function EnInsightsHub() {
  const { posts } = Route.useLoaderData() as { posts: EnInsightMeta[] };
  return (
    <div className="min-h-screen bg-background text-foreground" lang="en">
      <EnHeader />
      <main id="main-content">
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-16 text-center">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-xs font-semibold text-gold">
              <BookOpen className="h-3.5 w-3.5" /> Insights & Guides
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight md:text-5xl">
              Plain-English guides for expats in Thailand
            </h1>
            <p className="mx-auto mt-4 max-w-3xl text-base text-muted-foreground">
              Notary Public, Apostille, marriage registration, DTV digital
              nomad visa, and certified translation — written by licensed
              Phuket lawyers, updated for 2026.
            </p>
          </div>
        </section>

        <section>
          <div className="mx-auto grid max-w-5xl gap-6 px-4 py-12 md:grid-cols-2">
            {posts.map((p: EnInsightMeta) => (
              <Link
                key={p.slug}
                to="/en/insights/$slug"
                params={{ slug: p.slug }}
                className="group flex flex-col rounded-2xl border border-border bg-card p-6 transition hover:-translate-y-0.5 hover:border-gold hover:shadow-xl"
              >
                <div className="flex items-center gap-2 text-xs font-semibold text-gold">
                  <Tag className="h-3 w-3" />
                  {EN_INSIGHT_CATEGORY_LABEL[p.category]}
                </div>
                <h2 className="mt-2 text-lg font-bold leading-snug text-foreground group-hover:text-primary md:text-xl">
                  {p.title}
                </h2>
                <p className="mt-2 line-clamp-3 flex-1 text-sm text-muted-foreground">
                  {p.excerpt}
                </p>
                <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {new Date(p.publishedAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" /> {p.readingMinutes} min read
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
        <EnOfficesStrip />
        <EnCtaStrip />
      </main>
      <SiteFooter />
    </div>
  );
}